import React, { useState, useEffect } from 'react';
import { FiUsers, FiToggleLeft, FiToggleRight, FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import adminService from '../../services/admin.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (roleFilter) params.role = roleFilter;
      const { data } = await adminService.getUsers(params);
      setUsers(data.data);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [page, roleFilter]);

  const handleToggleActive = async (userId) => {
    try {
      const { data } = await adminService.toggleUserActive(userId);
      toast.success(data.message);
      setUsers(users.map((u) => u.id === userId ? { ...u, isActive: !u.isActive } : u));
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const roleBadgeColor = { customer: 'primary', merchant: 'warning', admin: 'danger' };

  return (
    <div className="section-padding">
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2><FiUsers className="me-2" />User Management</h2>
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm" style={{ width: 'auto' }}
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
            >
              <option value="">All Roles</option>
              <option value="customer">Customers</option>
              <option value="merchant">Merchants</option>
              <option value="admin">Admins</option>
            </select>
          </div>
        </div>

        {loading ? <LoadingSpinner /> : (
          <>
            <div className="dashboard-card">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Verified</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="fw-semibold">{user.firstName} {user.lastName}</td>
                        <td className="text-muted">{user.email}</td>
                        <td>
                          <span className={`badge bg-${roleBadgeColor[user.role] || 'secondary'}`}>
                            {user.role}
                          </span>
                        </td>
                        <td>
                          <span className={`badge bg-${user.isActive ? 'success' : 'danger'}`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge bg-${user.isEmailVerified ? 'success' : 'secondary'} bg-opacity-75`}>
                            {user.isEmailVerified ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="text-muted small">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <button
                            className={`btn btn-sm ${user.isActive ? 'btn-outline-danger' : 'btn-outline-success'}`}
                            onClick={() => handleToggleActive(user.id)}
                            title={user.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {user.isActive ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {pagination.pages > 1 && (
              <nav className="d-flex justify-content-center mt-3">
                <ul className="pagination">
                  {Array.from({ length: pagination.pages }, (_, i) => (
                    <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            <p className="text-muted small mt-2">
              Showing {users.length} of {pagination.total} users
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
